import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Languages } from "lucide-react";
import { RESUME_PDF } from "../data/content";

export const Navbar = ({ lang, setLang, t }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "#about", label: t.nav.about },
    { id: "#education", label: t.nav.education },
    { id: "#skills", label: t.nav.skills },
    { id: "#experience", label: t.nav.experience },
    { id: "#contact", label: t.nav.contact },
  ];

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "bg-[#050608]/70 backdrop-blur-xl border-b border-white/10 py-3" : "border-b border-transparent py-5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <a href="#top" data-testid="logo" className="font-display font-extrabold text-lg tracking-tight">
          Fagner<span className="text-[#2563eb]">.</span>dev
        </a>

        <div className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.id}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="font-mono text-[13px] text-[#8b95a5] hover:text-white transition-colors duration-300 relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#2563eb] group-hover:w-full transition-[width] duration-300" />
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "pt" ? "en" : "pt")}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 hover:border-[#2563eb] hover:bg-white/[0.03] transition-colors duration-300"
          >
            <Languages className="w-3.5 h-3.5 text-[#8b95a5]" />
            <span className="font-mono text-[12px] font-medium w-4 text-center overflow-hidden">
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={lang}
                  initial={{ y: 12, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -12, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="block uppercase"
                >
                  {lang}
                </motion.span>
              </AnimatePresence>
            </span>
          </button>

          <motion.a
            data-testid="download-resume-nav"
            href={RESUME_PDF}
            download
            whileTap={{ scale: 0.96 }}
            className="group flex items-center gap-2 px-4 py-2 rounded-full bg-[#2563eb] text-white font-mono text-[12px] font-medium hover:bg-[#1d4ed8] transition-colors duration-300"
          >
            <Download className="w-3.5 h-3.5 group-hover:translate-y-0.5 transition-transform duration-300" />
            <span className="hidden sm:inline">{t.nav.download}</span>
          </motion.a>
        </div>
      </div>
    </motion.nav>
  );
};
